import React, { useCallback, useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Animated, { FadeIn } from 'react-native-reanimated';

import { getCloudinaryUrl, INGREDIENT_IMAGE_CONFIG } from '@sg/garnish';
import { type Vendor } from '@sg/graphql-schema';

import { AddToBag } from '@order/features-customization';
import { useTelemetry } from '@order/Telemetry';

import { useNavigateBack } from '../../../../navigation';
import { useQuickAddCustomization } from '../../hooks';
import {
  QUICK_ADD_FADE_IN_DURATION,
  QUICK_ADD_STAGGER_DELAY,
} from '../../QuickAddScreen.constants';
import { QuickAddPrompt } from '../QuickAddPrompt';

// ─────────────────────────────────────────────────────────────────────────────

export const QuickAddCustomization = (props: Props) => {
  const {
    baseProductLineageKey,
    productSlug,
    productName,
    restaurantSlug,
    locationName,
    sharedLineItemId,
    addressId,
    deliveryFee,
    vendor,
    onAddToBagSuccess,
    onCustomize,
  } = props;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const navigateBack = useNavigateBack();

  const {
    hasFailedToFetch,
    hasFailedToAddToBag,
    outOfStockIngredients,
    isBlocking,
    shouldShowUnavailableIngredients,
    isAddingToBag,
    addToBag,
  } = useQuickAddCustomization({
    productSlug,
    restaurantSlug,
    sharedLineItemId,
    addressId,
    deliveryFee,
    vendor,
    onAddToBagSuccess,
    onCompletionBlocked: onCustomize,
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const unavailableIngredients = useMemo(
    () => outOfStockIngredients.map(toUnavailableIngredient).filter(checkIfDefined),
    [outOfStockIngredients],
  );

  const ingredients = unavailableIngredients.map(extractIngredientName).join(', ');
  const unavailableCount = unavailableIngredients.length;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleAddWithoutUnavailable = useCallback(() => {
    track('quick-add.add-without-unavailable-tapped', {
      baseProductLineageKey,
      sharedLineItemId,
      ingredients,
      isBlocking,
    });

    addToBag();
  }, [addToBag, baseProductLineageKey, ingredients, isBlocking, sharedLineItemId, track]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!shouldShowUnavailableIngredients) return;

    track('quick-add.unavailable-ingredients.viewed', {
      baseProductLineageKey,
      sharedLineItemId,
      ingredients,
      isBlocking,
    });
  }, [baseProductLineageKey, ingredients, isBlocking, sharedLineItemId, shouldShowUnavailableIngredients, track]); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  if (hasFailedToFetch) {
    return (
      <AddToBag.PromptContainer key="failed-to-load">
        <QuickAddPrompt
          title={formatMessage(messages.failedToLoadTitle)}
          description={formatMessage(messages.failedToLoadDescription)}
          ctaLabel={formatMessage(messages.close)}
          onCtaPress={navigateBack}
        />
      </AddToBag.PromptContainer>
    );
  }

  if (hasFailedToAddToBag) {
    return (
      <AddToBag.PromptContainer key="failed-to-add">
        <QuickAddPrompt
          title={formatMessage(messages.failedToAddTitle)}
          description={formatMessage(messages.failedToAddDescription)}
          ctaLabel={formatMessage(messages.close)}
          onCtaPress={navigateBack}
        />
      </AddToBag.PromptContainer>
    );
  }

  if (shouldShowUnavailableIngredients) {
    return (
      <AddToBag.UnavailableIngredients.Container key="unavailable-ingredients">
        <AddToBag.UnavailableIngredients.Content>
          <Animated.View entering={getStaggeredFadeIn(0)}>
            <AddToBag.UnavailableIngredients.Body
              count={unavailableCount}
              productName={productName}
              locationName={locationName}
              isReplacementRequired={isBlocking}
            />
          </Animated.View>

          <AddToBag.UnavailableIngredients.List>
            {unavailableIngredients.map((ingredient, index) => (
              <Animated.View key={ingredient.id} entering={getStaggeredFadeIn(index + 1)}>
                <AddToBag.UnavailableIngredients.Ingredient
                  name={ingredient.name}
                  imageUrl={ingredient.imageUrl}
                />
              </Animated.View>
            ))}
          </AddToBag.UnavailableIngredients.List>
        </AddToBag.UnavailableIngredients.Content>

        <Animated.View entering={getStaggeredFadeIn(unavailableCount + 1)}>
          <AddToBag.UnavailableIngredients.Footer shouldFitActionsInRow={isBlocking}>
            {isBlocking ? (
              <AddToBag.UnavailableIngredients.CancelButton
                disabled={isAddingToBag}
                onPress={navigateBack}
              />
            ) : (
              <AddToBag.UnavailableIngredients.ChooseNewButton
                count={unavailableCount}
                palette="primary"
                disabled={isAddingToBag}
                onPress={onCustomize}
              />
            )}

            {isBlocking ? (
              <AddToBag.UnavailableIngredients.ContinueButton
                disabled={isAddingToBag}
                onPress={onCustomize}
              />
            ) : (
              <AddToBag.UnavailableIngredients.AddAnywayButton
                isLoading={isAddingToBag}
                onPress={handleAddWithoutUnavailable}
              />
            )}
          </AddToBag.UnavailableIngredients.Footer>
        </Animated.View>
      </AddToBag.UnavailableIngredients.Container>
    );
  }

  return <AddToBag.Loading key="loading" />;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStaggeredFadeIn(step: number) {
  return FadeIn.duration(QUICK_ADD_FADE_IN_DURATION).delay(step * QUICK_ADD_STAGGER_DELAY);
}

function toUnavailableIngredient(modification: OutOfStockIngredient) {
  const { ingredient } = modification;

  if (!ingredient) return;

  return {
    id: ingredient.id,
    name: ingredient.name,
    imageUrl: getCloudinaryUrl(ingredient.asset.url, INGREDIENT_IMAGE_CONFIG),
  };
}

function extractIngredientName(ingredient: UnavailableIngredient) {
  return ingredient.name;
}

function checkIfDefined(
  ingredient: UnavailableIngredient | undefined,
): ingredient is UnavailableIngredient {
  return ingredient !== undefined;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  failedToLoadTitle: {
    defaultMessage: 'Something went wrong',
    description: 'Quick Add | Failed to load | Title',
  },
  failedToLoadDescription: {
    defaultMessage: 'We couldn’t load this item.',
    description: 'Quick Add | Failed to load | Description',
  },
  failedToAddTitle: {
    defaultMessage: 'Something went wrong',
    description: 'Quick Add | Failed to add to bag | Title',
  },
  failedToAddDescription: {
    defaultMessage: 'We couldn’t add this item to your bag.',
    description: 'Quick Add | Failed to add to bag | Description',
  },
  close: {
    defaultMessage: 'Close',
    description: 'Quick Add | Failed to load | Close CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  baseProductLineageKey: string;
  productSlug: string;
  productName: string;
  restaurantSlug: string;
  locationName: string;
  sharedLineItemId?: string;
  addressId?: string;
  deliveryFee?: number;
  vendor?: Vendor;
  onAddToBagSuccess: () => void;
  onCustomize: () => void;
};

type OutOfStockIngredient = ReturnType<
  typeof useQuickAddCustomization
>['outOfStockIngredients'][number];

type UnavailableIngredient = NonNullable<ReturnType<typeof toUnavailableIngredient>>;
